/**
 * アプリの設定の自動保存と復元（docs/08 §231）。
 *
 * iPhone のストレージ不足でアプリのデータが消え、端末ID・お気に入り・ゴールなどが無くなった（2026-11-01）。
 * 利用者と決めたこと:
 * - 設定を**タイムスタンプのように**ときどき保存する。**起動を遅くしないため、起動してすぐには保存しない**
 * - **使っていないとき**（しばらく画面に触れていない、または画面を閉じたとき）に、**約1時間ごと**
 * - 保存先は**端末の中とサーバー（GV Bridge、合言葉の内側）の両方**。データが消えても、**端末ID**を入れれば
 *   サーバーから取り戻せる
 *
 * 保存するのは `localStorage` の `gv4d-` で始まるキー（合言葉を通った印・取り込みのキャッシュは除く）。
 * 秘密の情報（合言葉・パスワード）は localStorage に置いていないので、入らない。
 */

const SNAPSHOTS_KEY = 'gv4d-settings-snapshots';
const META_KEY = 'gv4d-settings-backup-meta';

/** 保存の間隔 */
export const BACKUP_INTERVAL_MS = 60 * 60 * 1000;
/** 起動してから最初に保存してよくなるまで（起動を遅くしない） */
export const FIRST_BACKUP_DELAY_MS = 10 * 60 * 1000;
/** これだけ画面に触れていなければ「使っていない」 */
export const IDLE_MS = 60 * 1000;
/** 端末の中に残す数（新しい順） */
export const LOCAL_SNAPSHOTS = 3;

/** 保存しないキー: 合言葉を通った印（復元先で合言葉を入れ直させる）・取り込みのキャッシュ・この仕組み自身 */
const EXCLUDED_KEYS = new Set(['gv4d-app-unlocked', 'gv4d-official-pibal-cache', 'gv4d-official-pibal-seen', SNAPSHOTS_KEY, META_KEY]);
/** 1つの値・全体の上限（大きな記録で端末・サーバーを圧迫しない） */
const MAX_VALUE_CHARS = 200_000;
const MAX_TOTAL_CHARS = 900_000;

export interface SettingsSnapshot {
  savedAt: number;
  app: string;
  items: Record<string, string>;
}

type StorageLike = Pick<Storage, 'length' | 'key' | 'getItem'>;

/** 保存する設定を集める（キーの名前順） */
export function collectSettings(storage: StorageLike): Record<string, string> {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith('gv4d-') && !EXCLUDED_KEYS.has(k)) keys.push(k);
  }
  keys.sort();
  const items: Record<string, string> = {};
  let total = 0;
  for (const k of keys) {
    const v = storage.getItem(k);
    if (v == null || v.length > MAX_VALUE_CHARS || total + v.length > MAX_TOTAL_CHARS) continue;
    items[k] = v;
    total += v.length;
  }
  return items;
}

/** 保存してよい時刻か（起動直後でなく、前回から1時間たった） */
export function isBackupDue(nowMs: number, startedAtMs: number, lastStampAtMs: number): boolean {
  return nowMs - startedAtMs >= FIRST_BACKUP_DELAY_MS && nowMs - lastStampAtMs >= BACKUP_INTERVAL_MS;
}

export function sameItems(a: Record<string, string>, b: Record<string, string>): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

interface Meta {
  /** 最後に保存を確かめた時刻（中身が同じで保存しなかったときも進める） */
  lastStampAt: number;
  /** 最後にサーバーへ保存できた時刻 */
  lastServerAt: number | null;
  lastServerId: string | null;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 容量不足などで保存できなくても、アプリの動作は続ける
  }
}

export function loadLocalSnapshots(): SettingsSnapshot[] {
  return readJson<SettingsSnapshot[]>(SNAPSHOTS_KEY, []);
}
export function loadBackupMeta(): Meta {
  return readJson<Meta>(META_KEY, { lastStampAt: 0, lastServerAt: null, lastServerId: null });
}

/** 復元する。スナップショットのキーを書き戻す（スナップショットに無いキーはそのまま）。呼び出し側で再読み込みする */
export function applySnapshot(snap: SettingsSnapshot): void {
  for (const [k, v] of Object.entries(snap.items)) {
    if (!k.startsWith('gv4d-') || EXCLUDED_KEYS.has(k)) continue;
    try {
      localStorage.setItem(k, v);
    } catch {
      // 入りきらないものは飛ばす
    }
  }
}

export interface StampResult {
  local: 'saved' | 'same';
  server: 'saved' | 'same' | 'no-id' | 'error';
}

export class SettingsBackup {
  readonly #baseUrl: string;
  readonly #getTerminalId: () => string;
  readonly #app: string;
  readonly #startedAt = Date.now();
  #lastInputAt = Date.now();
  #busy = false;
  onStamp: ((r: StampResult, at: number) => void) | null = null;

  constructor(options: { baseUrl: string; getTerminalId: () => string; app: string }) {
    this.#baseUrl = options.baseUrl;
    this.#getTerminalId = options.getTerminalId;
    this.#app = options.app;
  }

  /** 見張りを始める。起動の処理が終わったあとに呼ぶ（ここでは何も保存しない） */
  start(): void {
    const touch = (): void => {
      this.#lastInputAt = Date.now();
    };
    for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
      window.addEventListener(type, touch, { passive: true, capture: true });
    }
    // 1分ごとに「時刻が来ていて、使っていない」かを見る
    setInterval(() => {
      const now = Date.now();
      if (now - this.#lastInputAt < IDLE_MS) return;
      if (isBackupDue(now, this.#startedAt, loadBackupMeta().lastStampAt)) void this.stamp();
    }, 60_000);
    // 画面を閉じた（ほかのアプリへ移った）ときも「使っていない」。時刻が来ていれば保存する
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden') return;
      if (isBackupDue(Date.now(), this.#startedAt, loadBackupMeta().lastStampAt)) void this.stamp();
    });
  }

  /** 今すぐ保存する（「今すぐ保存」ボタンと、見張りの両方から） */
  async stamp(): Promise<StampResult | null> {
    if (this.#busy) return null;
    this.#busy = true;
    try {
      const now = Date.now();
      const items = collectSettings(localStorage);
      const snap: SettingsSnapshot = { savedAt: now, app: this.#app, items };

      // 端末の中（中身が変わったときだけ増やす）
      const local = loadLocalSnapshots();
      let localResult: StampResult['local'] = 'same';
      if (!local[0] || !sameItems(local[0].items, items)) {
        writeJson(SNAPSHOTS_KEY, [snap, ...local].slice(0, LOCAL_SNAPSHOTS));
        localResult = 'saved';
      }

      // サーバー（端末IDが無ければ送らない。合言葉を通っていなければ401で黙って諦める）
      const meta = loadBackupMeta();
      const id = this.#getTerminalId().trim();
      let serverResult: StampResult['server'] = 'no-id';
      if (id) {
        try {
          const res = await fetch(`${this.#baseUrl}/api/settings-backup?id=${encodeURIComponent(id)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(snap),
            // 画面を閉じる途中でも送り切る
            keepalive: JSON.stringify(snap).length < 60_000,
          });
          if (res.ok) {
            const body = (await res.json()) as { saved?: boolean };
            serverResult = body.saved ? 'saved' : 'same';
            meta.lastServerAt = now;
            meta.lastServerId = id;
          } else serverResult = 'error';
        } catch {
          serverResult = 'error';
        }
      }
      meta.lastStampAt = now;
      writeJson(META_KEY, meta);
      const result = { local: localResult, server: serverResult };
      this.onStamp?.(result, now);
      return result;
    } finally {
      this.#busy = false;
    }
  }

  /** サーバーにある保存の一覧（新しい順） */
  async listServer(id: string): Promise<Array<{ savedAt: number; keys: number }>> {
    const res = await fetch(`${this.#baseUrl}/api/settings-backup?id=${encodeURIComponent(id.trim())}`);
    if (res.status === 401) throw new Error('合言葉の確認が必要です');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return ((await res.json()) as { snapshots: Array<{ savedAt: number; keys: number }> }).snapshots;
  }

  async fetchServer(id: string, savedAt: number): Promise<SettingsSnapshot> {
    const res = await fetch(`${this.#baseUrl}/api/settings-backup?id=${encodeURIComponent(id.trim())}&at=${savedAt}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as SettingsSnapshot;
  }
}
